/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description react query keys
 */
import _ from 'lodash';
import { QueryClient } from '@tanstack/react-query';

const QUERY_KEYS = {
	assignableRoles: 'query-assignable-roles',
	auditLogs: 'query-audit-logs',
	keycloakUsers: 'query-keycloak-users',
	userAttributes: 'query-user-attributes',
	// subscriptions
	billingInformation: 'query-billing-information',
	currentUsage: 'query-current-usage',
	addOns: 'query-addon-info',
	addOnStatuses: 'query-addon-statuses',
	activeSubscriptions: 'query-active-subscription',
	invoiceHistory: 'query-invoice-history',
	// eligibility
	eligibilityFormMetadata: 'query-eligibility-form-metadata',
	eligibilityRequests: 'query-eligibility-requests',
	eligibilityPatientID: 'query-eligibility-patient-id',
	selectedPayer: 'query-registered-payers',
	selectedPayersPerNpi: 'query-registered-payers-per-npi',
	payerMaintenance: 'query-payer-maintenance',
	eligibilityCount: 'query-eligibility-count',
	batchEligibilityRequest: 'query-batch-eligibility-request',
	batchRequestById: 'query-batch-request-by-id',
	batchResubmission: 'query-batch-resubmission',
	memberCardById: 'query-member-card-by-id',
	payerPortalJob: 'query-payer-portal-job',
	headerMappingSuggestion: 'query-header-mapping-suggestion',
	registeredPayers: 'query-registered-payers',
	serviceTypes: 'query-service-types',
	// claims
	claimStatusRequests: 'query-claim-status-requests',
	claimStatusResponse: 'query-claim-status-response',
	claimWorkLists: 'query-claim-work-lists',
	claimWorkListById: 'query-claim-work-list-by-id',
	claimWorkListEntities: 'query-claim-work-list-entities',
	claimRequestById: 'query-claim-request-by-id',
	claimRequestByPatientId: 'query-claim-request-by-patient-id',
	claimActivityType: 'query-claim-activity-type',
	claimActivityLogs: 'query-claim-activity-logs',
	claimResubmit: 'query-claim-resubmit',
	allERAs: 'query-all-eras',
	eraSummary: 'query-era-summary',
	eraDetails: 'query-era-details',
	claimPaymentReconciliation: 'query-claim-payment-reconciliation',
	secondaryClaimPayers: 'query-secondary-claim-payers',
	secondaryClaimPCN: 'query-secondary-claim-pcn',
	secondaryClaimSubmit: 'query-secondary-claim-submit',
	claimPatientCoverage: 'query-claim-patient-coverage',
	primaryClaimSubmit: 'query-primary-claim-submit',
	deleteDraftClaim: 'query-delete-draft-claim',

	// claim responses
	claimResponses: 'query-claim-responses',
	postPaymentDraftClaims: 'query-post-payment-draft-claims',

	// admin
	provider: 'query-provider',
	location: 'query-location',
	enrollmentData: 'query-enrollment-data',

	// settings
	generalSettings: 'query-general-settings',
	customOutputFile: 'query-custom-output-file',
	generalSettingsAccessSchema: 'query-general-settings-access-schema',
	customAttributes: 'query-custom-attributes',
	entityCustomAttributes: 'query-entity-custom-attributes',
	// payments
	blobData: 'query-blob-data',
	allPaymentAllocations: 'query-all-payment-allocations',
	paymentAllocation: 'query-payment-allocation',
	batchPostPaymentHistory: 'query-batch-post-payment-history',

	// workflow
	workflowDetails: 'query-workflow-details',
	organization: 'query-organization',

	// code explorer
	codeSuggestions: 'query-code-suggestions',
	searchCodes: 'query-search-codes',
	codeDetails: 'query-code-details',
	codeRelations: 'query-code-relations',
	codeHierarchy: 'query-code-hierarchy',
	codeTenantData: 'query-code-tenant-data',
	codeReactions: 'query-code-reactions',
	codeSummary: 'query-code-summary',
	cptDocument: 'query-cpt-document',
	codeModifiers: 'query-code-modifiers',

	// patients
	searchPatients: 'query-search-patients',
	patientInfo: 'query-patient-info',
	insurances: 'query-insurances',
	tenantPayers: 'query-tenant-payer',
	coverageEligibility: 'query-coverage-eligibility',
	searchEncounters: 'query-search-encounters',
	searchCharges: 'query-search-charges',
	searchChargesByEncounter: 'query-search-charges-by-encounter',

	// accounts
	accounts: 'query-accounts',
	accountClaims: 'query-account-claims',
	guarantors: 'query-guarantors',
	guarantorInfo: 'query-guarantor-info',
	createAccount: 'query-create-account',
	accountsByPatientId: 'query-accounts-by-patient-id',

	// analytics
	supersetGuestToken: 'query-superset-guest-token',

	// attachments
	allAttachments: 'query-all-attachments',

	// clearing house
	clearingHouse: 'query-clearing-house',

	// entity settings
	entitySettings: 'query-entity-settings',
	writeOffReasons: 'query-write-off-reasons',

	// features
	featureConfig: 'query-feature-config',
};

const metadataKeys = [
	QUERY_KEYS.eligibilityFormMetadata,
	QUERY_KEYS.selectedPayer,
	QUERY_KEYS.selectedPayersPerNpi,
	QUERY_KEYS.payerMaintenance,
	QUERY_KEYS.registeredPayers,
	QUERY_KEYS.tenantPayers,
	QUERY_KEYS.eligibilityRequests,
	QUERY_KEYS.provider,
	QUERY_KEYS.location,
];

const entityNoteKeys = [QUERY_KEYS.accountClaims, QUERY_KEYS.guarantorInfo, QUERY_KEYS.patientInfo, QUERY_KEYS.eraDetails];

const invalidateMetadata = (queryClient: QueryClient): void => {
	_.forEach(metadataKeys, (query) => queryClient.invalidateQueries({ queryKey: [query] }));
};

const invalidateEntityNote = (queryClient: QueryClient): void => {
	_.forEach(entityNoteKeys, (query) => queryClient.invalidateQueries({ queryKey: [query] }));
};

export { invalidateMetadata, invalidateEntityNote };
export default QUERY_KEYS;
